import {
  CellKeyDownEvent,
  ColDef,
  ICellEditorParams,
  ICellRendererParams,
  ProcessCellForExportParams,
  IRowNode,
  SuppressKeyboardEventParams,
  ValueFormatterParams,
  ValueGetterParams,
  ValueSetterParams,
  GridOptions,
} from 'ag-grid-community'
import {
  columnTypes,
  defaultOnCellClicked,
  frameworkComponents,
} from '../../../containers/commons/AgGrid'
import { DefaultCellRenderer } from '../../../containers/BulkSheetView/components/cellRenderer'
import _ from 'lodash'
import objects from '../../../../utils/objects'
import store from '../../../../store'
import { requireSave } from '../../../../store/requiredSaveData'
import { useColumnDefs } from './columnDefs'
import { WbsItemAdditionalPropertyNameCellRenderer } from '../../../containers/BulkSheetView/components/cellRenderer/WbsItemAdditionalPropertyNameCellRenderer'
import {
  AdditionalPropertyRow,
  getName,
  GroupHeaderRow,
  isAdditionalPropertyRow,
  isGroupHeaderRow,
  WbsItemAdditionalPropertyRow,
} from '../rowModel/wbsItemAdditionalPropertyRow'
import {
  propertyName,
  selectOptions,
  wbsItemTypes,
} from '../../../page-properties/bulksheet-properties/wbsItemAdditionalProperty'
import { ClientSideTextFilter } from '../../../containers/BulkSheetView/components/filter'
import { useMemo } from 'react'
import {
  I18nLabelVO,
  i18nLabelVoService,
} from '../../../../domain/value-object/I18nLabelVO'
import { I18nLabelCellEditor } from '../../../containers/BulkSheetView/components/cellEditor/I18nLabelCellEditor'
import { intl } from '../../../../i18n'
import { WbsItemTypeVO } from '../../../../domain/value-object/WbsItemTypeVO'
import { WbsItemAdditionalPropertySelectOption } from '../../../../domain/entity/WbsItemAdditionalPropertyEntity'
import { generateUuid } from '../../../../utils/uuids'

export const useWbsItemAdditionalPropertyGridOptions = (): GridOptions => {
  const columnDefs = useColumnDefs()
  const gridOptions = useMemo(
    () => ({
      groupHeaderHeight: 25,
      headerHeight: 45,
      rowHeight: 32,
      treeData: false,
      context: {},
      suppressLastEmptyLineOnPaste: true,
      // Row
      rowDragManaged: true,
      rowDragMultiRow: true,
      suppressMoveWhenRowDragging: true,
      enterNavigatesVerticallyAfterEdit: true,
      onCellKeyDown: (params: CellKeyDownEvent) => {
        // @ts-ignore
        if (!['Delete', 'Backspace'].includes(params.event.key)) return

        const cellRanges = params.api.getCellRanges() || []
        const rowNodesChangedNameCell: IRowNode[] = []
        cellRanges.forEach(range => {
          const start = Math.min(
            range.startRow!.rowIndex,
            range.endRow!.rowIndex
          )
          const end = Math.max(range.startRow!.rowIndex, range.endRow!.rowIndex)
          range.columns.forEach(column => {
            const colDef = column.getColDef()
            if (
              typeof colDef.editable === 'function'
                ? colDef.editable(params)
                : colDef.editable
            ) {
              for (let i = start; i <= end; i++) {
                const rowNode = params.api.getDisplayedRowAtIndex(i)
                if (!rowNode) continue
                if (column.getColId() === 'ag-Grid-AutoColumn') {
                  if (isAdditionalPropertyRow(rowNode.data)) {
                    const additionalPropertyRow =
                      rowNode.data as AdditionalPropertyRow
                    additionalPropertyRow.edited = true
                    additionalPropertyRow.propertyNameI18n =
                      i18nLabelVoService.clearLabels(
                        additionalPropertyRow.propertyNameI18n
                      )
                  } else if (isGroupHeaderRow(rowNode.data)) {
                    const groupHeaderRow = rowNode.data as GroupHeaderRow
                    groupHeaderRow.headerNameI18n =
                      i18nLabelVoService.clearLabels(
                        groupHeaderRow.headerNameI18n
                      )
                  }
                  rowNodesChangedNameCell.push(rowNode)
                } else {
                  rowNode.setDataValue(column, undefined)
                }
              }
            }
          })
        })
        params.api.refreshCells({
          rowNodes: rowNodesChangedNameCell,
          columns: ['ag-Grid-AutoColumn'],
        })
      },
      // Column
      columnTypes: columnTypes(),
      components: frameworkComponents,
      defaultColDef: {
        cellDataType: false,
        width: 85,
        editable: false,
        enableValue: false,
        sortable: true,
        resizable: true,
        suppressHeaderMenuButton: true,
        suppressSizeToFit: true,
        singleClickEdit: false,
        cellEditor: 'textEditor',
        cellStyle: { justifyContent: 'left' },
        cellRenderer: DefaultCellRenderer,
        onCellClicked: defaultOnCellClicked,
        suppressPaste: params => !_.isEmpty(params.context?.copied),
        suppressKeyboardEvent: (params: SuppressKeyboardEventParams) => {
          // When editing in Japanese, any keyboard event should be suppressed.
          if (params.editing && params.event.isComposing) {
            return true
          }
          // Prevent editing cell for DELETE or BACKSPACE
          return (
            !params.editing &&
            ['Delete', 'Backspace'].includes(params.event.key)
          )
        },
        valueSetter: (params: ValueSetterParams) => {
          const field = params.colDef.field || params.colDef.colId
          if (
            !field ||
            (!params.oldValue && !params.newValue) ||
            params.oldValue === params.newValue
          ) {
            return false
          }
          objects.setValue(params.data, field, params.newValue)
          params.data.edited = true
          store.dispatch(requireSave())
          return true
        },
      },
      columnDefs,
      // Row grouping
      // @ts-ignore
      treeData: true,
      excludeChildrenWhenTreeDataFiltering: true,
      groupDefaultExpanded: 1,
      autoGroupColumnDef: {
        headerName: propertyName.headerName,
        width: propertyName.width,
        pinned: true,
        editable: true,
        valueSetter: (params: ValueSetterParams) => {
          const newValue = params.newValue
          return nameValueSetter(params.data, newValue)
        },
        cellRendererParams: {
          suppressCount: true,
          suppressDoubleClickExpand: true,
          innerRenderer: WbsItemAdditionalPropertyNameCellRenderer,
          validate: (
            value: I18nLabelVO | undefined,
            params: ICellRendererParams
          ): string | undefined => {
            let nameI18n: I18nLabelVO
            if (isAdditionalPropertyRow(params.data)) {
              const additionalPropertyRow = params.node
                .data as AdditionalPropertyRow
              nameI18n = additionalPropertyRow.propertyNameI18n
            } else if (isGroupHeaderRow(params.data)) {
              const groupHeaderRow = params.node.data as GroupHeaderRow
              nameI18n = groupHeaderRow.headerNameI18n
            } else {
              return
            }
            if (nameI18n.labels.length === 0) {
              return intl.formatMessage({
                id: 'boolExpression.requiredField',
              })
            }
            const emptyLabels = nameI18n.labels.filter(v => !v.label)
            if (emptyLabels.length > 0) {
              return emptyLabels
                .map(v =>
                  intl.formatMessage(
                    {
                      id: 'wbsItemAdditionalProperties.validation.nameI18n.language.required',
                    },
                    { languageCode: v.languageCode }
                  )
                )
                .join('\n')
            }
            return undefined
          },
        },
        valueFormatter: (params: ValueFormatterParams) =>
          params.node ? getName(params.node.data) : '',
        valueGetter: (params: ValueGetterParams) =>
          params.node ? getName(params.node.data) : '',
        cellEditor: I18nLabelCellEditor,
        cellEditorParams: {
          valueGetter: (params: ICellEditorParams) => {
            if (!params.node) {
              return undefined
            }
            if (isGroupHeaderRow(params.node.data)) {
              const groupHeaderRow = params.node.data as GroupHeaderRow
              return groupHeaderRow.headerNameI18n
            } else {
              const additionalPropertyRow = params.node
                .data as AdditionalPropertyRow
              return additionalPropertyRow.propertyNameI18n
            }
          },
          valueSetter: (params: ICellEditorParams, newValue: I18nLabelVO) => {
            return nameValueSetter(params.data, newValue)
          },
        },
        floatingFilter: true,
        filter: ClientSideTextFilter,
        filterParams: {
          valueGetter: (params: { node; context }) => {
            if (!params.node) {
              return undefined
            }
            if (isGroupHeaderRow(params.node.data)) {
              const groupHeaderRow = params.node.data as GroupHeaderRow
              return i18nLabelVoService.getLabel(groupHeaderRow.headerNameI18n)
            } else {
              const additionalPropertyRow = params.node
                .data as AdditionalPropertyRow
              return i18nLabelVoService.getLabel(
                additionalPropertyRow.propertyNameI18n
              )
            }
          },
          filterChangedCallback: () => {
            return
          },
        },
        cellClassRules: {
          'hover-over-can-drop': params => {
            return (
              params.context?.onTree &&
              params.context?.draggableNodeId &&
              params.node?.id === params.context.draggableNodeId
            )
          },
        },
      },
      processCellForClipboard,
      processCellFromClipboard,
      processUnpinnedColumns: () => [],
    }),
    [columnDefs]
  )

  return gridOptions
}

export const processCellForClipboard = ({
  column,
  value,
}: ProcessCellForExportParams) => {
  const colDef = column.getColDef()
  const { field } = colDef
  if (!value) return value
  if (colDef.colId === 'ag-Grid-AutoColumn') {
    return value
  }
  if (field === wbsItemTypes.field) {
    if (Array.isArray(value)) {
      return value
        .filter(v => v instanceof WbsItemTypeVO)
        .map(v => v.name)
        .join(',')
    }
    return value
  }
  if (field === selectOptions.field) {
    if (Array.isArray(value)) {
      const options: WbsItemAdditionalPropertySelectOption[] = value
      return options
        .map(o => o.selectOption)
        .filter(o => !!o && 0 < o.length)
        .join(',')
    }
    return value
  }
  return value
}

const splitOptionString = (value: string) =>
  value?.split(',').filter((v: string) => !!v && 0 < v.trim().length)

const processCellFromClipboard = ({
  column,
  value,
  node,
}: ProcessCellForExportParams<AdditionalPropertyRow>) => {
  const colDef: ColDef = column.getColDef()
  const { field } = colDef
  if (colDef.colId === 'ag-Grid-AutoColumn') {
    if (!node) return
    const rowData = node?.data as WbsItemAdditionalPropertyRow
    let newNameI18n: I18nLabelVO = i18nLabelVoService.construct()
    if (isGroupHeaderRow(rowData)) {
      const groupHeaderRow = rowData as GroupHeaderRow
      if (groupHeaderRow.headerNameI18n) {
        newNameI18n = groupHeaderRow.headerNameI18n
      }
    } else if (isAdditionalPropertyRow(rowData)) {
      const additionalPropertyRow = rowData as AdditionalPropertyRow
      if (additionalPropertyRow.propertyNameI18n) {
        newNameI18n = additionalPropertyRow.propertyNameI18n
      }
    }
    return i18nLabelVoService.setCurrentLanguageLabel(newNameI18n, value)
  }
  if (field === wbsItemTypes.field) {
    const names = splitOptionString(value)
    const { wbsItemTypes, ticketTypes } = store.getState().project
    return [...wbsItemTypes.getAll(), ...ticketTypes].filter(v =>
      names.includes(v.name)
    )
  }
  if (field === selectOptions.field) {
    const optionNames = splitOptionString(value)
    return optionNames.map((name: string, index: number) => {
      return {
        uuid: generateUuid(),
        selectOption: name,
        displayOrder: index + 1,
      } as WbsItemAdditionalPropertySelectOption
    })
  }
  return value
}

const nameValueSetter = (
  rowData: WbsItemAdditionalPropertyRow,
  newValue: I18nLabelVO
): boolean => {
  if (isGroupHeaderRow(rowData)) {
    const groupHeaderRow = rowData as GroupHeaderRow
    if (i18nLabelVoService.hasDiff(groupHeaderRow.headerNameI18n, newValue)) {
      groupHeaderRow.headerNameI18n = newValue
      groupHeaderRow.edited = true
      store.dispatch(requireSave())
      return true
    }
  } else {
    const additionalPropertyRow = rowData as AdditionalPropertyRow
    if (
      i18nLabelVoService.hasDiff(
        additionalPropertyRow.propertyNameI18n,
        newValue
      )
    ) {
      additionalPropertyRow.propertyNameI18n = newValue
      additionalPropertyRow.edited = true
      store.dispatch(requireSave())
      return true
    }
  }
  return false
}
